
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchStudents } from "../../redux/actions/userManagementActions";
import { 
  FiSearch, 
  FiUser, 
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiDownload,
  FiX
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const Students = () => {
  const dispatch = useDispatch();
  const { 
    students, 
    paginationStudents: pagination, 
    loading, 
    error 
  } = useSelector((state) => state.userManagement);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Use a ref to track initial load
  const isInitialLoad = useRef(true);

  // Single useEffect to handle all API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchStudents({ 
        page: currentPage, 
        limit: 10, 
        full_name: searchTerm || undefined 
      }));
    }, isInitialLoad.current ? 0 : 500); // No delay for initial load

    isInitialLoad.current = false;
    
    return () => clearTimeout(timer);
  }, [searchTerm, currentPage, dispatch]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      // No need to dispatch here - useEffect will handle it
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    dispatch(fetchStudents({ 
      page: currentPage, 
      limit: 10, 
      full_name: searchTerm || undefined 
    }));
    toast.success("Student list refreshed");
  };

  // Get department from clg_roll
  const getDepartment = (clgRoll) => {
    if (!clgRoll) return "N/A";
    if (clgRoll.includes("BAH")) return "Arts";
    if (clgRoll.includes("BSCH")) return "Science";
    if (clgRoll.includes("BCOM")) return "Commerce";
    return "General";
  };

  // Get class/year from clg_roll
  const getClass = (clgRoll) => {
    if (!clgRoll) return "N/A";
    const yearPrefix = clgRoll.split('/')[0];
    if (yearPrefix === "25") return "1st Year";
    if (yearPrefix === "24") return "2nd Year";
    if (yearPrefix === "23") return "3rd Year";
    return `${yearPrefix} Year`;
  };

  // Handle view student details
  const handleViewStudent = (student) => {
    setSelectedStudent(student);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
    toast.success("Search cleared");
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (students.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      // Prepare data for export
      const exportData = students.map(student => ({
        "Full Name": student.full_name || "",
        "College ID": student.college_id || "",
        "College Roll": student.clg_roll || "",
        "CU Registration": student.cu_reg || "",
        "CU Roll": student.cu_roll || "",
        "Department": getDepartment(student.clg_roll),
        "Class/Year": getClass(student.clg_roll),
        "Status": "Active"
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Students");
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `GMGC_Students_${timestamp}.xlsx`;
      
      // Download the file
      XLSX.writeFile(wb, filename);
      
      toast.success(`Exported ${students.length} students to ${filename}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data. Please try again.");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiUser className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error Loading Students</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-xl transition-all hover:shadow-lg hover:shadow-purple-500/25"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Student Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage and monitor all students of Gokhale Memorial Girls' College
          </p>
        </div>
        <div className="flex items-center gap-4 mt-4 md:mt-0">
          <button
            onClick={handleExportCSV}
            disabled={students.length === 0}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
              students.length === 0
                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                : "bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white hover:shadow-lg hover:shadow-emerald-500/25"
            }`}
          >
            <FiDownload />
            Export Excel
          </button>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2.5 rounded-xl transition-all hover:shadow-lg hover:shadow-purple-500/25"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 mb-8">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search students by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition text-gray-900 dark:text-white"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { title: "Total Students", value: pagination.total || 0, color: "from-blue-500 to-cyan-500", icon: <FiUser /> },
          { title: "Current Page", value: `${currentPage} of ${pagination.totalPages || 1}`, color: "from-purple-500 to-pink-500", icon: <FiDownload /> },
          { title: "Arts Students", value: students.filter(s => getDepartment(s.clg_roll) === "Arts").length, color: "from-emerald-500 to-green-500", icon: <FiUser /> },
          { title: "Science Students", value: students.filter(s => getDepartment(s.clg_roll) === "Science").length, color: "from-amber-500 to-orange-500", icon: <FiUser /> },
        ].map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: idx * 0.1 }}
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 dark:border-gray-700 overflow-hidden group"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5 group-hover:opacity-10 transition-opacity`} />
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">
                    {stat.title}
                  </p>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </h2>
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} text-white shadow-lg`}>
                  {stat.icon}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Students Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Student List
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                Showing {students.length} of {pagination.total || 0} students
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-sm font-medium">
                Page {currentPage}
              </span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading students...</p>
          </div>
        ) : students.length === 0 ? (
          <div className="py-20 text-center">
            <FiUser className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Students Found</h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              {searchTerm ? `No students found matching "${searchTerm}"` : "No students available"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      College ID & Roll
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Department & Class
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {students.map((student, index) => (
                    <motion.tr
                      key={student.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors group cursor-pointer"
                      onClick={() => handleViewStudent(student)}
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-md group-hover:scale-105 transition-transform">
                            {student.full_name?.charAt(0) || 'S'}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {student.full_name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              ID: {student.college_id}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <p className="text-gray-900 dark:text-white font-medium">
                            {student.college_id}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Roll: {student.clg_roll || "N/A"}
                          </p>
                          {(student.cu_reg || student.cu_roll) && (
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              CU: {student.cu_reg} {student.cu_roll}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-2">
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium">
                            {getDepartment(student.clg_roll)}
                          </span>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {getClass(student.clg_roll)}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          Active
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, pagination.total || 0)} of {pagination.total || 0} students
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${
                      currentPage === 1
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    <FiChevronLeft />
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination.totalPages || 1) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      if (pageNum < 1 || pageNum > pagination.totalPages) return null;
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-lg transition ${
                            currentPage === pageNum
                              ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
                              : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${
                      currentPage === pagination.totalPages
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    Next
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Student Detail Modal */}
      <AnimatePresence>
        {selectedStudent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Student Details
                  </h3>
                  <button
                    onClick={() => setSelectedStudent(null)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition text-gray-500 dark:text-gray-400"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* Student Profile */}
                  <div className="flex flex-col items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl">
                    <div className="w-24 h-24 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                      {selectedStudent.full_name?.charAt(0) || 'S'}
                    </div>
                    <div className="text-center">
                      <h4 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {selectedStudent.full_name}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400 mt-1">
                        {selectedStudent.college_id}
                      </p>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500 dark:text-gray-400">College Roll</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {selectedStudent.clg_roll || "N/A"}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500 dark:text-gray-400">CU Registration</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {selectedStudent.cu_reg || "N/A"}
                        </p>
                      </div>
                    </div>
                    
                    {selectedStudent.cu_roll && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500 dark:text-gray-400">CU Roll Number</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {selectedStudent.cu_roll}
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Department</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {getDepartment(selectedStudent.clg_roll)}
                        </p>
                      </div>
                      <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Class/Year</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {getClass(selectedStudent.clg_roll)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <button 
                      onClick={() => setSelectedStudent(null)}
                      className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 py-3 rounded-xl font-medium transition-all"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Students;